.scale--s {
  --calcite-slider-handle-size: theme("spacing[2.5]");
  --calcite-slider-handle-extension-height: 0.4rem;
  --calcite-slider-container-font-size: var(--calcite-font-size--3);
  .handle__label,
  .tick__label {
    @apply leading-3;
  }
}

.scale--m {
  --calcite-slider-handle-size: theme("spacing[3.5]");
  --calcite-slider-handle-extension-height: 0.5rem;
  --calcite-slider-container-font-size: var(--calcite-font-size--2);
  .handle__label,
  .tick__label {
    @apply leading-4;
  }
}

.scale--l {
  --calcite-slider-handle-size: 1rem;
  --calcite-slider-handle-extension-height: 0.65rem;
  --calcite-slider-container-font-size: var(--calcite-font-size--1);
  .handle__label,
  .tick__label {
    @apply leading-4;
  }
}

.handle__label,
.tick__label {
  @apply text-color-2 font-medium;
  font-size: var(--calcite-slider-container-font-size);
}

:host {
  @apply block;
}

.container {
  @apply relative block break-normal;
  padding-inline: calc(var(--calcite-slider-handle-size) * 0.5);
  padding-block: calc(var(--calcite-slider-handle-size) * 0.5);
  margin-block: calc(var(--calcite-slider-handle-size) * 0.5);
  margin-inline: 0;
  --calcite-slider-full-handle-height: calc(
    var(--calcite-slider-handle-size) + var(--calcite-slider-handle-extension-height)
  );
  touch-action: none;
  // disable browser swipe navigation to prevent interference with the slide thumb following a touch gesture
}

@include disabled() {
  .track__range,
  .tick--active {
    background-color: var(--calcite-color-text-3);
  }
}

.scale--s {
  .thumb:not(.thumb--precise) {
    --calcite-slider-thumb-y-offset: -0.375rem;
  }
}
.scale--m {
  .thumb:not(.thumb--precise) {
    --calcite-slider-thumb-y-offset: -0.5rem;
  }
}
.scale--l {
  .thumb:not(.thumb--precise) {
    --calcite-slider-thumb-y-offset: -0.55rem;
  }
}

:host([precise]:not([has-histogram])) {
  .container .thumb--value {
    --calcite-slider-thumb-y-offset: calc(var(--calcite-slider-full-handle-height) * -1);
  }
}

.thumb-container {
  @apply relative max-w-full;
}

.thumb {
  --calcite-slider-thumb-x-offset: calc(var(--calcite-slider-handle-size) * 0.5);
  @apply font-inherit
    absolute
    m-0
    flex
    cursor-pointer
    flex-col
    items-center
    border-none
    bg-transparent
    p-0
    outline-none;
  transform: translate(var(--calcite-slider-thumb-x-offset), var(--calcite-slider-thumb-y-offset));

  .handle__label {
    &.static,
    &.transformed {
      @apply absolute
        top-0
        bottom-0
        opacity-0;
    }
    &.hyphen::after {
      content: "\2014";
      display: inline-block;
      inline-size: 1em;
    }
    &.hyphen--wrap {
      display: flex;
    }
  }

  .handle {
    @apply focus-base
      bg-foreground-1
      box-border
      rounded-full;
    block-size: var(--calcite-slider-handle-size);
    inline-size: var(--calcite-slider-handle-size);
    box-shadow: 0 0 0 2px var(--calcite-color-text-3) inset;
    transition: border var(--calcite-internal-animation-timing-medium) ease,
      background-color var(--calcite-internal-animation-timing-medium) ease,
      box-shadow var(--calcite-animation-timing) ease;
  }

  .handle-extension {
    @apply w-0.5;
    block-size: var(--calcite-slider-handle-extension-height);
    background-color: var(--calcite-color-text-3);
  }

  &:hover {
    .handle {
      box-shadow: 0 0 0 3px var(--calcite-color-brand) inset;
    }
    .handle-extension {
      @apply bg-brand;
    }
  }

  &:focus {
    .handle {
      @apply focus-outset;
    }
    .handle-extension {
      @apply bg-brand;
    }
  }

  &.thumb--minValue {
    transform: translate(calc(var(--calcite-slider-thumb-x-offset) * -1), var(--calcite-slider-thumb-y-offset));
  }

  &.thumb--precise {
    --calcite-slider-thumb-y-offset: -0.125rem;
  }
}

:host([label-handles]) {
  .thumb {
    --calcite-slider-thumb-x-offset: 50%;
  }
  &:host(:not([has-histogram])) {
    .scale--s {
      .thumb:not(.thumb--precise) {
        --calcite-slider-thumb-y-offset: -1.4375rem;
      }
    }
    .scale--m {
      .thumb:not(.thumb--precise) {
        --calcite-slider-thumb-y-offset: -1.875rem;
      }
    }
    .scale--l {
      .thumb:not(.thumb--precise) {
        --calcite-slider-thumb-y-offset: -2rem;
      }
    }
  }
}

:host([has-histogram][label-handles]),
:host([label-handles]:not([has-histogram])) .thumb--minValue.thumb--precise {
  .handle__label {
    margin-block-start: 0.5em;
  }
}
:host(:not([has-histogram]):not([precise])),
:host([label-handles]:not([has-histogram])) .thumb--value {
  .handle__label {
    margin-block-end: 0.5em;
  }
}

:host([label-handles][precise]) {
  &:host(:not([has-histogram])) .scale--s .thumb--value {
    --calcite-slider-thumb-y-offset: -2.075rem;
  }
  &:host(:not([has-histogram])) .scale--m .thumb--value {
    --calcite-slider-thumb-y-offset: -2.75rem;
  }
  &:host(:not([has-histogram])) .scale--l .thumb--value {
    --calcite-slider-thumb-y-offset: -3.0625rem;
  }
}

.thumb:focus,
.thumb--active {
  .handle {
    @apply bg-brand;
    box-shadow: 0 0 8px 0 rgb(0 0 0 / 16%); // shadow 1 press
  }
}

.thumb:hover.thumb--precise:after,
.thumb:focus.thumb--precise:after,
.thumb--active.thumb--precise:after {
  @apply bg-brand;
}

.track {
  @apply relative h-0.5 rounded-none;
  background-color: var(--calcite-color-border-2);
  transition: all var(--calcite-internal-animation-timing-medium) ease-in;
}

.track__range {
  @apply bg-brand
  absolute
  top-0
  h-0.5;
}
.container--range {
  .track__range {
    &:hover {
      cursor: ew-resize;
    }
    &:after {
      @apply absolute w-full;
      content: "";
      inset-block-start: calc(calc(var(--calcite-slider-full-handle-height) * 0.5) * -1);
      block-size: calc(var(--calcite-slider-handle-size) + var(--calcite-slider-handle-extension-height));
    }
  }
}

@media (forced-colors: active) {
  .thumb {
    outline-width: 0;
    outline-offset: 0;
  }
  .handle {
    @apply outline-none;
  }
  .thumb:focus .handle,
  .thumb .handle-extension,
  .thumb:hover .handle-extension,
  .thumb:focus .handle-extension,
  .thumb:active .handle-extension {
    background-color: canvasText;
  }
  .track {
    background-color: canvasText;
  }
  .track__range {
    background-color: highlight;
  }
}

.tick {
  @apply absolute
    h-1
    w-0.5
    border
    border-solid;
  background-color: var(--calcite-color-border-input);
  border-color: var(--calcite-color-foreground-1);
  inset-block-start: -2px;
  pointer-events: none;
  margin-inline-start: calc(-1 * theme("margin[0.5]"));
}

.tick--active {
  @apply bg-brand;
}

.tick__label {
  @apply pointer-events-none
    mt-3.5
    flex
    justify-center;
}

.tick__label--min {
  transition: opacity var(--calcite-animation-timing);
}

.tick__label--max {
  transition: opacity var(--calcite-internal-animation-timing-fast);
}

:host([has-histogram][label-handles]),
:host([has-histogram][precise]) {
  .tick__label--min,
  .tick__label--max {
    @apply text-color-3 font-normal;
  }
}

.graph {
  color: var(--calcite-color-foreground-3);
  block-size: 48px;
}

// Ensure we have enough space on variants with text elements to prevent overlap
:host([label-ticks][ticks]) {
  .container {
    // match tick__label top margin + font-size
    padding-block-end: calc(theme("margin[3.5]") + var(--calcite-slider-container-font-size));
  }
}
:host([has-histogram]) {
  &:host([precise][label-handles]) {
    .container {
      padding-block-end: calc(var(--calcite-slider-full-handle-height) + 1em);
    }
  }
  &:host([label-handles]:not([precise])) {
    .container {
      padding-block-end: calc(calc(var(--calcite-slider-handle-size) * 0.5) + 1em);
    }
  }
  &:host([precise]:not([label-handles])) {
    .container {
      padding-block-end: var(--calcite-slider-full-handle-height);
    }
  }
}
:host(:not([has-histogram])) {
  &:host([precise]:not([label-handles])) {
    .container {
      padding-block-start: var(--calcite-slider-full-handle-height);
    }
    .container--range {
      padding-block-end: var(--calcite-slider-full-handle-height);
    }
  }
  &:host([label-handles]:not([precise])) {
    .container {
      padding-block-start: calc(var(--calcite-slider-full-handle-height) + 4px);
    }
  }
  &:host([label-handles][precise]) {
    .container {
      padding-block-start: calc(
        var(--calcite-slider-full-handle-height) + var(--calcite-slider-container-font-size) + 4px
      );
    }
    .container--range {
      padding-block-end: calc(
        var(--calcite-slider-full-handle-height) + var(--calcite-slider-container-font-size) + 4px
      );
    }
  }
}

@include hidden-form-input();
@include base-component();
